import React, { useState } from 'react';
import { IioTable } from '../../../components/IioTable';
import {
  useSearchTerms,
  useCreateSearchTerm,
  useUpdateSearchTerm,
  useDeleteSearchTerm,
  useSearchTermJobs,
  useRunSearchTermJob,
  useCancelSearchTermJob,
  SearchTermList,
} from '../../../hooks/useSearchTerms';
import { AlertBlock } from '../../../components/AlertBlock';
import { LoadingPlaceholder } from '../../../components/LoadingPlaceholder';
import { formatLocaleDate } from '../../../utilities/date';
import { formatNumber } from '../../../utilities/format';
import { broadcastBasicSearch } from '../../../services/angular-bridge';
import { TermRow } from './TermRow';

interface Props {
  projectId: string;
  list: SearchTermList;
  onBack: () => void;
}

export function ListDetail({ projectId, list, onBack }: Props) {
  const { data: termsData, isLoading: loadingTerms } = useSearchTerms(projectId, list.id);
  const { data: jobsData } = useSearchTermJobs(projectId, list.id, list.status === 'Running');
  const createTerm = useCreateSearchTerm(projectId, list.id);
  const updateTerm = useUpdateSearchTerm(projectId, list.id);
  const deleteTerm = useDeleteSearchTerm(projectId, list.id);
  const runJob = useRunSearchTermJob(projectId, list.id);
  const cancelJob = useCancelSearchTermJob(projectId, list.id);

  const [newTerm, setNewTerm] = useState('');

  const terms = termsData?.searchTerms ?? [];
  const jobs = jobsData?.searchTermJobs ?? [];
  const isRunning = jobs.some(j => j.status === 'Running');
  const allValid = terms.length > 0 && terms.every(t => t.isValid);
  const isBusy = runJob.isPending || cancelJob.isPending;
  const saving = createTerm.isPending || updateTerm.isPending || deleteTerm.isPending;
  const showViewAll = terms.length > 0 || jobs.length > 0;

  const handleAddTerm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTerm.trim()) return;
    createTerm.mutate(newTerm.trim(), { onSuccess: () => setNewTerm('') });
  };

  return (
    <div>
      <br />
      <div className="row">
        <div className="col-md-12">
          <button className="btn btn-default" onClick={onBack}>← Back</button>
          {' '}
          <button
            className="btn btn-primary"
            onClick={() => runJob.mutate()}
            disabled={!allValid || isRunning || isBusy}
          >
            <i className="fas fa-running" aria-hidden="true" /> Run Search Term List
          </button>
          {' '}
          <button
            className="btn btn-default"
            onClick={() => cancelJob.mutate()}
            disabled={!isRunning || cancelJob.isPending}
          >
            <i className="fas fa-stop" aria-hidden="true" /> Cancel
          </button>
        </div>
      </div>

      {jobs.length > 0 && (
        <div className="row">
          <div className="col-md-12">
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              <IioTable>
                <colgroup>
                  <col /><col /><col /><col /><col /><col />
                  <col style={{ width: 150 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Total Doc Hits</th>
                    <th>Unique Parent Hits</th>
                    <th>Unique Hits</th>
                    <th>Unique Hits with Family</th>
                    <th>Report</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.id} className={job.status === 'Running' ? 'active' : ''}>
                      <td>{formatLocaleDate(job.createdAt)}</td>
                      <td><small className="badge badge-success">{job.status}</small></td>
                      <td>{formatNumber(job.totalDocHits)}</td>
                      <td>{formatNumber(job.uniqueParentHits)}</td>
                      <td>{formatNumber(job.uniqueHits)}</td>
                      <td>{formatNumber(job.uniqueHitsWithFamily)}</td>
                      <td>
                        {job.directDownloadPath && (
                          <a href={job.directDownloadPath} download title="Download">
                            <i className="fas fa-download" aria-hidden="true" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </IioTable>
            </div>
          </div>
        </div>
      )}

      {!allValid && terms.length > 0 && (
        <div className="row">
          <div className="col-md-4">
            <br />
            <AlertBlock type="danger">
              <i className="fas fa-info-circle" aria-hidden="true" /> Cannot run until all terms are valid.
            </AlertBlock>
          </div>
        </div>
      )}

      <br />

      <div className="row">
        <div className="col-md-12">
          {showViewAll && (
            <p className="text-right" style={{ paddingRight: 75 }}>
              <a href="#" onClick={e => { e.preventDefault(); broadcastBasicSearch(`searchTags:"${list.tag.id}"`); }}>view all</a>
            </p>
          )}
          {loadingTerms ? <LoadingPlaceholder /> : (
            <table className="table">
              <colgroup>
                <col />
                <col style={{ width: 200, textAlign: 'center' }} />
              </colgroup>
              <tbody>
                {terms.map(term => (
                  <TermRow
                    key={term.id}
                    term={term}
                    list={list}
                    disabled={isRunning || isBusy || saving}
                    onUpdate={t => updateTerm.mutate(t)}
                    onDelete={id => deleteTerm.mutate(id)}
                  />
                ))}
                <tr>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add new search term"
                      value={newTerm}
                      onChange={e => setNewTerm(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTerm(e as any); } }}
                      disabled={isRunning || isBusy}
                      autoComplete="off"
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddTerm}
                      disabled={!newTerm.trim() || isRunning || isBusy || createTerm.isPending}
                    >
                      Add Search Term <i className="fas fa-plus-circle" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
